import React, { useState, useEffect } from "react";
import { Layout } from "../components";
import { Link, Navigate } from "react-router-dom";
import { fetchAllCompanies } from "../api/strapi/companies";
import { useIntl } from "react-intl";
import DataLoader from "../components/loader/DataLoader";
import { useLanguage } from "../context";
import { getLocalizedPath } from "../helpers/routes";
import PageHero from "../components/common/PageHero";
import SectionHeader from "../components/common/SectionHeader";
import SkeletonImage from "../components/common/SkeletonImage";

const Companies = () => {
  const intl = useIntl();
  const { language } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoading(true);
        const result = await fetchAllCompanies();

        if (!result.ok) {
          if (result.reason !== "NOT_FOUND") {
            console.warn("Companies data unavailable:", result);
            setError(intl.formatMessage({ id: "common.contentUnavailable" }));
          }
          setCompanies([]);
          return;
        }

        setCompanies(Array.isArray(result.data) ? result.data : []);
      } catch (err) {
        console.error("Erreur lors du chargement des entreprises:", err);
        setError(intl.formatMessage({ id: "common.contentUnavailable" }));
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
  }, [intl]);

  if (loading) {
    return (
      <Layout data={{ title: intl.formatMessage({ id: "common.loading" }) }}>
        <DataLoader isLoading={true} />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout data={{ title: intl.formatMessage({ id: "common.unavailable" }) }}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-slate-500 text-center">
            <h2 className="text-2xl font-bold mb-2 text-[#0a1628]">{intl.formatMessage({ id: "common.contentUnavailableTitle" })}</h2>
            <p>{error}</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Redirect to individual page if only one company exists
  if (companies.length === 1) {
    const companyData = companies[0].attributes || companies[0];
    return <Navigate to={getLocalizedPath(`/info/company/${companyData.slug}`, language)} replace />;
  }

  return (
    <Layout data={{ title: intl.formatMessage({ id: "company.title", defaultMessage: "Company" }) }}>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <PageHero
          eyebrow="Africa Systems"
          title={intl.formatMessage({ id: "company.title", defaultMessage: "Our Company" })}
          description={intl.formatMessage({ id: "company.description", defaultMessage: "Discover more about our company, mission, and values." })}
        />

        {/* Companies Grid */}
        <div className="py-16">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
            <SectionHeader
              eyebrow="Africa Systems"
              title={intl.formatMessage({ id: "company.title", defaultMessage: "Our Company" })}
              description={intl.formatMessage({ id: "company.description", defaultMessage: "Discover more about our company, mission, and values." })}
            />
            {companies.length === 0 ? (
              <div className="text-center py-12">
                <h2 className="text-2xl font-semibold text-[#0a1628] mb-4">
                  {intl.formatMessage({ id: "company.noneAvailable", defaultMessage: "No companies available" })}
                </h2>
                <p className="text-slate-500">
                  {intl.formatMessage({ id: "company.checkBackLater", defaultMessage: "Please check back later for more information." })}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {companies.map((company) => {
                  const companyData = company.attributes || company;
                  return (
                    <Link
                      key={company.id || companyData.id}
                      to={getLocalizedPath(`/info/company/${companyData.slug}`, language)}
                      className="block group"
                    >
                      <div className="group bg-white border border-slate-200 hover:border-[#16A34A]/40 hover:shadow-lg transition-all duration-300 h-full flex flex-col overflow-hidden">
                        {companyData.heroComponent?.image && (
                          <SkeletonImage
                            className="h-48"
                            src={`${process.env.REACT_APP_STRAPI_URL || 'http://156.67.104.166:1338'}${companyData.heroComponent.image.url}`}
                            alt={companyData.heroComponent.title}
                            imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            overlay={<div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />}
                          />
                        )}

                        <div className="p-6 flex flex-col flex-grow">
                          <h3 className="text-xl font-bold text-[#0a1628] mb-2 group-hover:text-[#16A34A] transition-colors">
                            {companyData.heroComponent?.title || companyData.title}
                          </h3>

                          <p className="text-slate-600 mb-4 flex-grow">
                            {companyData.heroComponent?.description || companyData.description}
                          </p>

                          <div className="mt-auto">
                            <span className="inline-flex items-center gap-2 bg-[#16A34A] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] hover:bg-[#15803D] transition-colors w-fit">
                              {intl.formatMessage({ id: "company.learnMore", defaultMessage: "Learn More" })}
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Companies;
